import { useState, useEffect } from "react";
import { Button, Modal, Table } from "@mantine/core";
import { Plus } from "tabler-icons-react";
import { useSearchParams } from "react-router-dom";

import { Supplier } from "@captiva/shared-types";
import { SupplierAssetConfig } from "../../components/listings/configurations/assets/supplier-asset-config";
import { AssetEditor } from "../../components/shared/asset-editor";
import { FlexOptionsChoiceModal } from "./shared/flex-options-choice-modal";
import { useCreateFlexOptionsSet } from "../../hooks/useCreateFlexOptionsSet";
import { bulkGenerateAndDownloadQR } from "../../components/shared/bulk-qr-generator";
import TableRowActions from "./shared/table-row-actions";
import { BulkActionsMenu } from "./shared/bulk-actions-menu";

export interface AuthUser {
  uid: string;
  email?: string;
  claims?: {
    companyId?: string;
  };
}

interface SupplierTableProps {
  elements: Supplier[];
  auth: AuthUser | null;
  reloadCallback: () => void;
}

export function SupplierTable({ elements, auth, reloadCallback }: SupplierTableProps) {
  const newSupplier: Partial<Supplier> = {
    name: "",
    isActive: true,
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [editedElement, setEditedElement] = useState<Supplier | Partial<Supplier> | undefined>(undefined);

  useEffect(() => {
    const editId = searchParams.get('editId');
    if (editId) {
      const match = elements.find((el) => el.id === editId);
      if (match) {
        setEditedElement(match);
      }
    }
  }, [searchParams, elements]);

  const closeEdit = () => {
    setEditedElement(undefined);
    if (searchParams.get('editId')) {
      searchParams.delete('editId');
      setSearchParams(searchParams, { replace: true });
    }
  };
  const [createFlexOptionsSet, { loading, notification, clearNotification, existingDocs, mergeOptions, createNewSet }] = useCreateFlexOptionsSet();
  
  const handleCreateFlexOptionsSet = () => {
    const companyId = auth?.claims?.companyId;
    if (!companyId) {
      alert("No company ID found in auth claims.");
      return;
    }
    createFlexOptionsSet({
      assetType: "Suppliers",
      assetList: elements,
      metadataPrefix: "supplier",
      fieldMappings: {
        label: "name",
        value: "id",
        metadata: {
          name: "name",
        }
      },
      companyId
    });
  };
  const rows = elements.map((element) => (
    <tr key={element.id}>
      <td>{element.name}</td>
      <td>{String(element.isActive ?? true)}</td>
      <td>
        <TableRowActions
          element={element}
          setEditedElement={setEditedElement}
          reloadCallback={reloadCallback}
          assetName="Supplier"
          collectionName="suppliers"
          nameKey="name"
          idKey="id"
        />
      </td>
    </tr>
  ));

  async function handleBulkQRDownload() {
    await bulkGenerateAndDownloadQR({
      assetName: "Supplier",
      items: elements,
      nameKey: "name",
      idKey: "id",
      zipName: "suppliers-qr-codes.zip",
      logoSrc: "/captiva-mark.png"
    });
  }
  return (
    <>
      {notification && notification.type !== "choice" && (
        <div style={{ position: "fixed", top: 76, right: 20, zIndex: 9999 }}>
          <Button color={notification.type === "success" ? "green" : "red"} variant="light" onClick={clearNotification}>
            {notification.message}
          </Button>
        </div>
      )}
      {elements.length > 0 && (
        <Table width="100%">
          <thead>
            <tr>
              <th>Name</th>
              <th>Active</th>
              <th style={{textAlign: 'left'}}>
                <BulkActionsMenu onBulkQRDownload={handleBulkQRDownload} onCreateFlexOptionsSet={handleCreateFlexOptionsSet} loading={loading} />
              </th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </Table>
      )}
      {editedElement && (
        <Modal opened onClose={closeEdit} title="Supplier">
          <AssetEditor
            asset={editedElement}
            assetConfig={SupplierAssetConfig}
            collection="suppliers"
            auth={auth}
            reloadCallback={() => {reloadCallback(); closeEdit();}}
          ></AssetEditor>
        </Modal>
      )}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Button onClick={() => setEditedElement(newSupplier)} fullWidth style={{ maxWidth: 500 }} my="xl" color="cap-navy" variant="outline" leftIcon={<Plus />}>
          Create new
        </Button>
      </div>
      {notification && notification.type === "choice" && (
        <FlexOptionsChoiceModal
          opened={true}
          onClose={() => clearNotification()}
          onMerge={mergeOptions}
          onCreateNew={createNewSet}
          existingDocs={existingDocs}
          assetType="Suppliers"
        />
      )}
    </>
  );
}
